<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import {
    refDebounced,
    useDocumentVisibility,
    useIntervalFn,
    useLocalStorage,
    useNow,
  } from '@vueuse/core';
  import { ReservationsService } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ReservationsNotice from '../V2ReservationsNotice.vue';
  import { isReservationSetupIncomplete } from '@/domain/reservations/reservation-setup';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import {
    reservationMatchesSearch,
    reservationStatusCounts,
  } from '@/domain/reservations/reservation-day-grouping';
  import { getReservationTimeZone } from '@/domain/reservations/reservation-time';
  import type { IReservation } from '@/interfaces/IReservation';
  import { useReservationsStore } from '@/stores/reservations.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2ReservationsCommandBar from './V2ReservationsCommandBar.vue';
  import V2ReservationsStatusChips from './V2ReservationsStatusChips.vue';
  import V2ReservationsDayList from './V2ReservationsDayList.vue';
  import V2ReservationsTimeline from './V2ReservationsTimeline.vue';
  import V2ReservationsFloor from './V2ReservationsFloor.vue';
  const props = defineProps<{
    date: string;
    today: string;
    canPrint: boolean;
    canSettings: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'date', date: string): void;
    (event: 'select', id: number): void;
    (
      event: 'create',
      prefill?: { tableIds?: number[]; startsAt?: string }
    ): void;
    (event: 'walkIn'): void;
    (event: 'print'): void;
    /** Open the step-by-step guide to finish the setup. */
    (event: 'setup'): void;
  }>();
  const store = useReservationsStore();
  const restaurant = useRestaurantStore();
  const modals = useModal();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const now = useNow({ interval: 30_000 });
  const visibility = useDocumentVisibility();
  const viewPreference = useLocalStorage('adminReservationsView', 'list');
  const view = computed(() =>
    ['list', 'timeline', 'floor'].includes(viewPreference.value)
      ? viewPreference.value
      : 'list'
  );
  const search = ref('');
  const commandBar = ref<{ focusSearch: () => Promise<void> } | null>(null);
  function focusSearch() {
    return commandBar.value?.focusSearch();
  }
  defineExpose({ focusSearch });
  const query = refDebounced(search, 200);
  const status = ref('all');
  const onlineBusy = ref(false);
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const rows = computed(() => store.day?.reservations ?? []);
  // Older API builds omit shiftsCount; fall back to the day's windows then.
  const shiftsCount = computed(() => store.day?.settingsSummary.shiftsCount);
  const closedDay = computed(
    () =>
      !!store.day &&
      shiftsCount.value !== undefined &&
      shiftsCount.value > 0 &&
      store.day.shifts.length === 0
  );
  const setupIncomplete = computed(() => {
    if (!store.day) return false;
    return isReservationSetupIncomplete(
      shiftsCount.value,
      store.day.shifts.length,
      restaurant.tables
        ? restaurant.tables.filter(
            (table) =>
              table.restaurantId === restaurant.restaurant?.id &&
              table.branchId === restaurant.selectedBranch?.id
          )
        : null
    );
  });
  const counts = computed(() => reservationStatusCounts(rows.value));
  const onlineOpen = computed(
    () =>
      !!store.day?.settingsSummary.onlineEnabled &&
      !store.day.overrideToday?.closed &&
      !store.day.overrideToday?.onlineClosed
  );
  const filtered = computed(() =>
    rows.value.filter((row) => matchesStatus(row) && matchesSearch(row))
  );
  const common = computed(() => ({
    reservations: filtered.value,
    shifts: store.day?.shifts ?? [],
    now: now.value,
    timeZone: zone.value,
    toleranceMin: store.day?.settingsSummary.toleranceMin ?? 15,
    businessDayStartHour: restaurant.selectedBranch?.businessDayStartHour,
    selectedId: store.selectedReservationId,
  }));
  let disposed = false;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }
  function matchesStatus(row: IReservation) {
    if (status.value === 'all') return true;
    if (status.value === 'pending')
      return ['pending', 'pending_payment'].includes(row.status);
    if (status.value === 'upcoming') return row.status === 'confirmed';
    if (status.value === 'present')
      return ['arrived', 'seated'].includes(row.status);
    if (status.value === 'cancelled')
      return ['cancelled', 'no_show'].includes(row.status);
    return row.status === 'finished';
  }
  function matchesSearch(row: IReservation) {
    return reservationMatchesSearch(row, query.value);
  }
  async function reload() {
    try {
      await store.loadDay(props.date, { silent: !!store.day });
    } catch (error) {
      if (!disposed)
        ui.serverError(error, i18nT('reservations.page.loadError'));
    }
  }
  async function toggleOnline(open: boolean) {
    const rid = restaurant.restaurant?.id;
    const bid = restaurant.selectedBranch?.id;
    const date = props.date;
    if (!rid || !bid || onlineBusy.value) return;
    if (
      !open &&
      !(await modals.confirm({
        title: i18nT('reservations.bar.onlineCloseTitle'),
        message: i18nT('reservations.bar.onlineCloseMessage'),
        acceptText: i18nT('reservations.bar.onlineCloseCta'),
        cancelText: i18nT('cancel'),
      }))
    )
      return;
    if (
      disposed ||
      bid !== restaurant.selectedBranch?.id ||
      date !== props.date
    )
      return;
    onlineBusy.value = true;
    try {
      await ReservationsService.upsertOverride(rid, bid, date, {
        onlineClosed: !open,
      });
      if (
        !disposed &&
        bid === restaurant.selectedBranch?.id &&
        date === props.date
      )
        await reload();
    } catch (error) {
      if (!disposed && bid === restaurant.selectedBranch?.id)
        ui.serverError(error);
    } finally {
      if (!disposed) onlineBusy.value = false;
    }
  }
  const timer = useIntervalFn(() => {
    if (visibility.value === 'visible') void store.refreshActive();
  }, 60_000);
  watch(
    visibility,
    (visible) => {
      if (visible === 'visible') {
        timer.resume();
        void store.refreshActive();
      } else timer.pause();
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    disposed = true;
    timer.pause();
  });
</script>

<template>
  <div class="reservations-day-tab">
    <V2ReservationsCommandBar
      ref="commandBar"
      :date="date"
      :today="today"
      :view="view"
      :search="search"
      :online-open="onlineOpen"
      :online-busy="
        onlineBusy ||
        !store.day ||
        !store.day.settingsSummary.onlineEnabled ||
        !!store.day.overrideToday?.closed
      "
      :can-print="canPrint"
      @date="emit('date', $event)"
      @view="viewPreference = $event"
      @search="search = $event"
      @online="toggleOnline"
      @create="emit('create')"
      @walkIn="emit('walkIn')"
      @print="emit('print')"
    />
    <V2ReservationsStatusChips v-model="status" :counts="counts" />
    <V2ReservationsNotice
      v-if="setupIncomplete"
      class="setup-notice"
      :text="$t('reservations.notice.setupIncomplete')"
      :action-label="
        canSettings ? $t('reservations.notice.openGuide') : undefined
      "
      @action="emit('setup')"
    />
    <div class="day-content">
      <div
        v-if="!store.day && store.dayStatus === 'loading'"
        class="day-loading"
        aria-busy="true"
      >
        <PosUiSkeleton v-for="index in 6" :key="index" class="row-skeleton" />
      </div>
      <PosUiEmptyState
        v-else-if="!store.day && store.dayStatus === 'error'"
        icon="alert-circle-outline"
        :title="$t('reservations.page.loadError')"
        ><template #action
          ><PosUiButton icon="refresh" @click="reload">{{
            $t('reservations.page.retry')
          }}</PosUiButton></template
        ></PosUiEmptyState
      >
      <template v-else-if="store.day">
        <div v-if="store.dayStatus === 'error'" class="day-error" role="alert">
          <span>{{ $t('reservations.page.loadError') }}</span
          ><PosUiButton color="neutral" variant="ghost" @click="reload">{{
            $t('reservations.page.retry')
          }}</PosUiButton>
        </div>
        <V2ReservationsDayList
          v-if="view === 'list'"
          v-bind="common"
          :filtered="status !== 'all' || !!query.trim()"
          :closed-day="closedDay"
          @select="emit('select', $event)"
          @create="emit('create')"
        />
        <V2ReservationsTimeline
          v-else-if="view === 'timeline'"
          v-bind="common"
          :date="date"
          @select="emit('select', $event)"
          @create="emit('create', $event)"
        />
        <V2ReservationsFloor
          v-else
          v-bind="common"
          :occupancy-reservations="rows"
          :date="date"
          :today="today"
          @select="emit('select', $event)"
          @create="emit('create', $event)"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-day-tab {
    @apply tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col;
    .day-content {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-auto;
    }
    .day-loading {
      @apply tw-p-4;
      .row-skeleton {
        @apply tw-mb-3 tw-h-14 tw-w-full;
      }
    }
    .day-error {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-red-50 tw-px-4 tw-text-sm tw-text-red-700;
    }
  }
</style>
